// The Teams screen (TASK-578, SCR-573, REQ-201): a library screen both shells mount. The org's
// teams as a table (name, the id the proxy sees, members), a row opening its members by name, and
// — for a principal who may write — New team, Rename and Delete (an empty team only). Tenancy is
// the `OrgContext` given; every call names its `orgId`. Writes fail closed: the list changes only
// from what the server answered. Read-only mode disables all write controls (TASK-579).
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { useReadOnly } from "../../host/readonly";
import { ConfirmDialog } from "../../primitives/dialog";
import { Card } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { Loading } from "../../primitives/states";
import { StaleNote } from "../../primitives/stale-note";
import { DataTable, type Column } from "../../primitives/table";
import type { ScopeMe } from "../../scope/scope";
import {
  asApiError,
  canWriteTeams,
  deleteBlockReason,
  memberRows,
  PAGE_LIMIT,
  teamRows,
  toCreateRequest,
  toRenameRequest,
  writeFailure,
  type Team,
  type TeamFormValues,
  type TeamRow,
  type User,
} from "./model";
import { TeamDialog } from "./team-dialog";

const COLUMNS: readonly Column<TeamRow>[] = [
  { key: "name", header: "Team", cell: (row) => row.name, compare: (a, b) => a.name.localeCompare(b.name) },
  { key: "externalId", header: "Team id", help: "teams.externalId", cell: (row) => <span class="mono">{row.externalId}</span> },
  {
    key: "members",
    header: "Members",
    numeric: true,
    cell: (row) => (row.members === null ? "—" : row.members),
    compare: (a, b) => (a.members ?? -1) - (b.members ?? -1),
  },
];

export interface TeamsScreenProps {
  context: OrgContext;
  me: ScopeMe;
}

export function TeamsScreen({ context, me }: TeamsScreenProps) {
  const client = useApi();
  const { orgId } = context;
  const { readOnly, reason } = useReadOnly();
  const canWrite = canWriteTeams(me);
  const [teams, setTeams] = useState<Team[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  /** Every user of the org, for the member counts; null when they could not be loaded. */
  const [users, setUsers] = useState<User[] | null>([]);
  const [open, setOpen] = useState<Team | null>(null);
  const [creating, setCreating] = useState(false);

  const loadTeams = (after: string | null) => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/teams", { path: { orgId }, query: after ? { cursor: after, limit: PAGE_LIMIT } : { limit: PAGE_LIMIT } })
      .then((page) => {
        setTeams((current) => (after ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
      })
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };

  const loadUsers = () => {
    client
      .get("/v1/orgs/{orgId}/users", { path: { orgId }, query: { limit: PAGE_LIMIT } })
      .then((page) => setUsers(page.items))
      .catch(() => setUsers(null));
  };

  useEffect(() => {
    loadTeams(null);
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId]);

  const replace = (team: Team) => setTeams((current) => current.map((t) => (t.id === team.id ? team : t)));
  const remove = (team: Team) => setTeams((current) => current.filter((t) => t.id !== team.id));

  return (
    <>
      {readOnly && <StaleNote id="teams-stale-note" />}
      <Card
        title="Teams"
        help="teams.list"
        actions={
          canWrite && (
            <button type="button" class="button button-primary button-sm" disabled={readOnly} title={readOnly ? reason ?? "" : undefined} aria-describedby={readOnly ? "teams-stale-note" : undefined} onClick={() => setCreating(true)}>
              New team
            </button>
          )
        }
      >
      <p class="card-note">Groups of users for the usage reports. Members join a team through the team_id claim, an AWS sign-in team mapping, or People.</p>
      <DataTable
        caption="Teams"
        hideCaption
        columns={COLUMNS}
        rows={teamRows(teams, users)}
        rowKey={(row) => row.id}
        loading={loading}
        error={error}
        onRetry={() => loadTeams(null)}
        emptyMessage="No teams yet. Teams come from the team_id claim, an AWS sign-in team mapping, or an admin."
        nextCursor={cursor}
        onLoadMore={() => loadTeams(cursor)}
        onRowClick={(row) => setOpen(teams.find((t) => t.id === row.id) ?? null)}
        rowLabel={(row) => `Open the team ${row.name}`}
      />
      {open && (
        <TeamDetail
          context={context}
          team={open}
          canWrite={canWrite}
          readOnly={readOnly}
          reason={reason}
          onClose={() => setOpen(null)}
          onRenamed={(team) => {
            replace(team);
            setOpen(team);
          }}
          onDeleted={(team) => {
            remove(team);
            setOpen(null);
          }}
        />
      )}
      {creating && (
        <TeamDialog
          mode="create"
          initial={{ name: "", externalId: "" }}
          submit={(values) => client.post("/v1/orgs/{orgId}/teams", { path: { orgId }, body: toCreateRequest(values) })}
          onSaved={(team) => {
            setTeams((current) => [...current, team]);
            setCreating(false);
          }}
          onClose={() => setCreating(false)}
          readOnly={readOnly}
          reason={reason}
        />
      )}
    </Card>
    </>
  );
}

interface TeamDetailProps {
  context: OrgContext;
  team: Team;
  canWrite: boolean;
  readOnly: boolean;
  reason: string | null;
  onClose(): void;
  onRenamed(team: Team): void;
  onDeleted(team: Team): void;
}

/** One team's summary: its members by name, and (for a writer) Rename and Delete. */
function TeamDetail({ context, team, canWrite, readOnly, reason, onClose, onRenamed, onDeleted }: TeamDetailProps) {
  const client = useApi();
  const { orgId } = context;
  /** undefined while loading; null when the members could not be loaded. */
  const [members, setMembers] = useState<User[] | null | undefined>(undefined);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    setMembers(undefined);
    client
      .get("/v1/orgs/{orgId}/users", { path: { orgId }, query: { teamId: team.id, limit: PAGE_LIMIT } })
      .then((page) => setMembers(page.items))
      .catch(() => setMembers(null));
  }, [client, orgId, team.id]);

  const count = members === undefined ? undefined : members === null ? null : members.length;
  const blockReason = count === undefined ? "Loading the members…" : deleteBlockReason(count);
  const path = { orgId, teamId: team.id };

  const confirmDelete = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await client.delete("/v1/orgs/{orgId}/teams/{teamId}", { path });
      setDeleting(false);
      onDeleted(team);
    } catch (error) {
      setFailure(writeFailure(asApiError(error)).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <RowDetail
        title={team.name}
        open
        onClose={onClose}
        facts={[
          { label: "Team id", value: <span class="mono">{team.externalId}</span> },
          { label: "Members", value: count ?? (count === null ? "—" : "…") },
        ]}
        actions={
          canWrite && (
            <>
              <button type="button" class="button button-secondary" disabled={readOnly} title={readOnly ? reason ?? "" : undefined} aria-describedby={readOnly ? "teams-stale-note" : undefined} onClick={() => setRenaming(true)}>
                Rename
              </button>
              <button
                type="button"
                class="button button-danger"
                disabled={readOnly || blockReason !== null}
                title={readOnly ? reason ?? "" : (blockReason ?? undefined)}
                aria-describedby={readOnly ? "teams-stale-note" : (blockReason ? "team-delete-note" : undefined)}
                onClick={() => setDeleting(true)}
              >
                Delete
              </button>
            </>
          )
        }
      >
        <h3 class="team-members-title">Members</h3>
        <Members members={members} />
        {canWrite && blockReason && (
          <p id="team-delete-note" class="card-note">
            {blockReason}
          </p>
        )}
      </RowDetail>
      {renaming && (
        <TeamDialog
          mode="rename"
          initial={{ name: team.name, externalId: team.externalId }}
          submit={(values: TeamFormValues) => client.patch("/v1/orgs/{orgId}/teams/{teamId}", { path, body: toRenameRequest(values) })}
          onSaved={(renamed) => {
            setRenaming(false);
            onRenamed(renamed);
          }}
          onClose={() => setRenaming(false)}
          readOnly={readOnly}
          reason={reason}
        />
      )}
      <ConfirmDialog
        title={`Delete the team ${team.name}?`}
        open={deleting}
        message={
          <>
            <p>The team is removed from the usage reports. Nobody belongs to it, so no one is affected.</p>
            {failure && <p role="alert">{failure}</p>}
          </>
        }
        confirmLabel="Delete team"
        busy={busy}
        onConfirm={confirmDelete}
        onClose={() => {
          setDeleting(false);
          setFailure(null);
        }}
      />
    </>
  );
}

function Members({ members }: { members: User[] | null | undefined }) {
  if (members === undefined) return <Loading label="Loading the members…" rows={2} />;
  if (members === null) return <p class="card-note">The members could not be loaded.</p>;
  if (members.length === 0) return <p class="card-note">No members yet. People join through the team_id claim, a team mapping, or People.</p>;
  return (
    <ul class="team-members">
      {memberRows(members).map((member) => (
        <li key={member.id} class="team-member">
          <span>{member.name}</span>
          {member.email && <span class="cell-sub">{member.email}</span>}
        </li>
      ))}
    </ul>
  );
}
